import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Download, ClipboardList, FileWarning, UserX } from 'lucide-react';
import * as XLSX from 'xlsx';
import { api } from '../../services/api';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

// Mismo criterio de paleta fija que RhIndicadoresModule.jsx — la identidad
// de cada tipo de inspección depende de esta posición, no de cuáles
// aparezcan en el rango de fechas elegido.
const PALETTE = {
  blue: '#2a78d6',
  orange: '#eb6834',
  aqua: '#1baf7a',
  yellow: '#eda100',
  magenta: '#e87ba4',
  green: '#008300',
  violet: '#4a3aa7',
  red: '#e34948',
};

const INSPECTION_LABELS = {
  sustancias_quimicas: 'Sustancias químicas',
  saneamiento_basico: 'Saneamiento básico',
  kit_derrames: 'Kit derrames',
  respel: 'RESPEL',
};
const INSPECTION_COLORS = {
  sustancias_quimicas: PALETTE.blue,
  saneamiento_basico: PALETTE.aqua,
  kit_derrames: PALETTE.orange,
  respel: PALETTE.magenta,
};

const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function toYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function monthLabel(key) {
  const [y, m] = key.split('-');
  const idx = Number(m) - 1;
  return `${MONTH_LABELS[idx] ?? m} ${y}`;
}

function inRange(dateStr, from, to) {
  const d = String(dateStr || '').slice(0, 10);
  if (!d) return false;
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
}

function daysBetween(startStr, endStr) {
  const start = new Date(String(startStr || '').slice(0, 10));
  const end = new Date(String(endStr || startStr || '').slice(0, 10));
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  const diff = Math.round((end - start) / 86400000) + 1;
  return diff > 0 ? diff : 1;
}

const chartMargin = { top: 8, right: 12, bottom: 8, left: 8 };

function CustomTooltip({ active, payload, label, suffix }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md px-3 py-2 text-xs">
      <div className="font-medium text-gray-800 mb-0.5">{label}</div>
      {payload.map((p, idx) => (
        <div key={idx} className="text-gray-600">
          {p.name ? `${p.name}: ` : ''}
          <span className="font-semibold text-gray-800">{p.value}{suffix || ''}</span>
        </div>
      ))}
    </div>
  );
}

export default function IndicadoresAmbientalesModule({ location }) {
  const today = useMemo(() => new Date(), []);
  const startOfYear = useMemo(() => new Date(today.getFullYear(), 0, 1), [today]);

  const [filters, setFilters] = useState({ from: toYMD(startOfYear), to: toYMD(today) });
  const [applied, setApplied] = useState({ from: toYMD(startOfYear), to: toYMD(today) });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [inspections, setInspections] = useState([]);
  const [waste, setWaste] = useState([]);
  const [basePodas, setBasePodas] = useState([]);
  const [services, setServices] = useState([]);
  const [species, setSpecies] = useState([]);
  const [absenteeism, setAbsenteeism] = useState([]);

  // Los 6 endpoints de SGI no soportan rango de fechas del lado del
  // servidor (solo filtran por ubicación) — se trae todo con un límite
  // amplio y el rango elegido se aplica en el navegador, igual que ya
  // hacía Almacén > Alertas para su propio listado.
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [insRes, wasteRes, podasRes, servRes, specRes, absRes] = await Promise.all([
        api.sgi.inspections.list({ location, limit: 3000, order: 'desc' }),
        api.sgi.waste.list({ location, limit: 3000, order: 'desc' }),
        api.sgi.basePodas.list({ location, limit: 3000, order: 'desc' }),
        api.sgi.services.list({ location, limit: 3000, order: 'desc' }),
        api.sgi.species.list({ location, limit: 3000, order: 'desc' }),
        api.sgi.absenteeism.list({ location, limit: 3000, order: 'desc' }),
      ]);
      setInspections(Array.isArray(insRes?.data) ? insRes.data : []);
      setWaste(Array.isArray(wasteRes?.data) ? wasteRes.data : []);
      setBasePodas(Array.isArray(podasRes?.data) ? podasRes.data : []);
      setServices(Array.isArray(servRes?.data) ? servRes.data : []);
      setSpecies(Array.isArray(specRes?.data) ? specRes.data : []);
      setAbsenteeism(Array.isArray(absRes?.data) ? absRes.data : []);
    } catch (e) {
      setError(e?.message || 'No fue posible cargar los indicadores ambientales.');
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => { load(); }, [load]);

  const { from, to } = applied;

  const inspectionsInRange = useMemo(
    () => inspections.filter((r) => !r.deleted_at && inRange(r.inspection_date, from, to)),
    [inspections, from, to]
  );
  const wasteInRange = useMemo(
    () => waste.filter((r) => !r.deleted_at && inRange(r.inspection_date, from, to)),
    [waste, from, to]
  );
  const podasInRange = useMemo(
    () => basePodas.filter((r) => !r.deleted_at && inRange(r.report_date, from, to)),
    [basePodas, from, to]
  );
  const servicesInRange = useMemo(
    () => services.filter((r) => !r.deleted_at && inRange(r.service_date, from, to)),
    [services, from, to]
  );
  const absenteeismInRange = useMemo(
    () => absenteeism.filter((r) => !r.deleted_at && inRange(r.start_date, from, to)),
    [absenteeism, from, to]
  );
  const speciesActive = useMemo(() => species.filter((r) => !r.deleted_at), [species]);

  // ---- Tipos de novedad (inspecciones ambientales) ----
  const inspectionsByType = useMemo(() => {
    const map = new Map();
    inspectionsInRange.forEach((r) => {
      const key = String(r.inspection_type || '').trim() || 'otro';
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([key, count]) => ({ key, label: INSPECTION_LABELS[key] || key, count, color: INSPECTION_COLORS[key] || PALETTE.red }));
  }, [inspectionsInRange]);

  const inspectionsMonthly = useMemo(() => {
    const map = new Map();
    inspectionsInRange.forEach((r) => {
      const key = String(r.inspection_date || '').slice(0, 7);
      if (!key) return;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, count]) => ({ key, label: monthLabel(key), count }));
  }, [inspectionsInRange]);

  // ---- Permisos / licencias (gestión de residuos) ----
  // valid_license es texto libre (no un select con "Sí"/"No"), así que el
  // indicador honesto es completitud del registro, no un semáforo de
  // vigencia que no se puede verificar de forma confiable.
  const permisos = useMemo(() => {
    const conRegistro = wasteInRange.filter((r) => String(r.valid_license || '').trim() !== '').length;
    return { total: wasteInRange.length, conRegistro, sinRegistro: wasteInRange.length - conRegistro };
  }, [wasteInRange]);

  // ---- Ausentismo (seguimiento de personal — SGI, no RH) ----
  const absenteeismTotalDays = useMemo(
    () => absenteeismInRange.reduce((acc, r) => acc + daysBetween(r.start_date, r.end_date), 0),
    [absenteeismInRange]
  );
  const absenteeismMonthly = useMemo(() => {
    const map = new Map();
    absenteeismInRange.forEach((r) => {
      const key = String(r.start_date || '').slice(0, 7);
      if (!key) return;
      map.set(key, (map.get(key) || 0) + daysBetween(r.start_date, r.end_date));
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, days]) => ({ key, label: monthLabel(key), days }));
  }, [absenteeismInRange]);
  const absenteeismByArea = useMemo(() => {
    const map = new Map();
    absenteeismInRange.forEach((r) => {
      const area = String(r.person_area || '').trim() || 'Sin área';
      map.set(area, (map.get(area) || 0) + daysBetween(r.start_date, r.end_date));
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([area, days], idx) => ({ area, days, color: Object.values(PALETTE)[idx % Object.values(PALETTE).length] }));
  }, [absenteeismInRange]);

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(inspectionsInRange.map((r) => ({
      fecha: r.inspection_date || '', tipo: INSPECTION_LABELS[r.inspection_type] || r.inspection_type || '', notas: r.notes || '',
    }))), 'Inspecciones');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(wasteInRange.map((r) => ({
      fecha: r.inspection_date || '', residuo: r.waste_name || '', tipo: r.waste_type || '', kg: r.quantity_kg || '', licencia_permiso: r.valid_license || '',
    }))), 'Permisos residuos');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(absenteeismInRange.map((r) => ({
      colaborador: r.person_name || '', area: r.person_area || '', desde: r.start_date || '', hasta: r.end_date || '', dias: daysBetween(r.start_date, r.end_date),
    }))), 'Ausentismo');
    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'sgi', reportName: 'Indicadores ambientales' });
    XLSX.writeFile(wb, `indicadores_ambientales_${applied.from}_${applied.to}.xlsx`);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h4 className="text-base font-semibold text-gray-900">Indicadores ambientales</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Inspecciones, permisos/licencias de residuos y ausentismo del área — periodo seleccionado.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-[11px] text-gray-500 uppercase tracking-wide mb-1">Desde</label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              className="border rounded-lg px-3 py-2 text-sm bg-white"
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 uppercase tracking-wide mb-1">Hasta</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              className="border rounded-lg px-3 py-2 text-sm bg-white"
            />
          </div>
          <button
            type="button"
            onClick={() => setApplied({ ...filters })}
            disabled={loading}
            className="px-3 py-2 rounded-lg text-sm text-white disabled:opacity-50"
            style={{ background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' }}
          >
            Aplicar
          </button>
          <button
            type="button"
            onClick={exportExcel}
            disabled={loading}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Exportar Excel
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
      )}

      {/* Resumen de actividad — un vistazo rápido a los 5 módulos hermanos */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Inspecciones</div>
          <div className="text-2xl font-semibold mt-1">{loading ? '...' : inspectionsInRange.length}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Base podas</div>
          <div className="text-2xl font-semibold mt-1">{loading ? '...' : podasInRange.length}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Residuos gestionados</div>
          <div className="text-2xl font-semibold mt-1">{loading ? '...' : wasteInRange.length}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Servicios controlados</div>
          <div className="text-2xl font-semibold mt-1">{loading ? '...' : servicesInRange.length}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-200">
          <div className="text-[11px] text-gray-600 uppercase tracking-wide">Especies registradas</div>
          <div className="text-2xl font-semibold mt-1">{loading ? '...' : speciesActive.length}</div>
          <div className="text-xs text-gray-400 mt-1">total, sin filtro de fecha</div>
        </div>
      </div>

      {/* Tipos de novedad + tendencia mensual (inspecciones) */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-3">
            <ClipboardList size={16} className="text-emerald-600" /> Tipos de novedad (inspecciones)
          </div>
          {inspectionsByType.length === 0 ? (
            <div className="text-sm text-gray-500 py-10 text-center">{loading ? 'Cargando...' : 'Sin inspecciones en el periodo.'}</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={inspectionsByType} margin={chartMargin}>
                <CartesianGrid strokeDasharray="0" vertical={false} stroke="#e1e0d9" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Bar dataKey="count" name="Cantidad" radius={[4, 4, 0, 0]} maxBarSize={30}>
                  {inspectionsByType.map((entry) => (
                    <Cell key={entry.key} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="text-sm font-semibold text-gray-800 mb-3">Inspecciones por mes</div>
          {inspectionsMonthly.length === 0 ? (
            <div className="text-sm text-gray-500 py-10 text-center">{loading ? 'Cargando...' : 'Sin inspecciones en el periodo.'}</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={inspectionsMonthly} margin={chartMargin}>
                <CartesianGrid strokeDasharray="0" vertical={false} stroke="#e1e0d9" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(42,120,214,0.06)' }} />
                <Bar dataKey="count" name="Inspecciones" fill={PALETTE.blue} radius={[4, 4, 0, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Permisos / licencias — gestión de residuos */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-1">
          <FileWarning size={16} className="text-amber-600" /> Permisos y licencias (gestión de residuos)
        </div>
        <p className="text-xs text-gray-500 mb-3">
          "Licencia / permiso / autorización vigente" es un campo de texto libre — este indicador mide qué
          tan completo está el registro, no un semáforo automático de vencimiento.
        </p>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl border border-gray-200 p-3">
            <div className="text-[11px] text-gray-600 uppercase tracking-wide">Registros del periodo</div>
            <div className="text-xl font-semibold mt-1">{loading ? '...' : permisos.total}</div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <div className="text-[11px] text-emerald-700 uppercase tracking-wide">Con permiso/licencia registrado</div>
            <div className="text-xl font-semibold mt-1 text-emerald-700">{loading ? '...' : permisos.conRegistro}</div>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
            <div className="text-[11px] text-amber-700 uppercase tracking-wide">Sin registrar</div>
            <div className="text-xl font-semibold mt-1 text-amber-700">{loading ? '...' : permisos.sinRegistro}</div>
          </div>
        </div>
      </div>

      {/* Ausentismo — seguimiento de personal (SGI) */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-4 xl:col-span-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-3">
            <UserX size={16} className="text-red-600" /> Ausentismo
          </div>
          <div className="text-2xl font-semibold" style={{ color: PALETTE.red }}>{loading ? '...' : absenteeismTotalDays}</div>
          <div className="text-xs text-gray-600 mt-1">días registrados en el periodo ({loading ? '...' : absenteeismInRange.length} casos)</div>
          <div className="text-[11px] text-gray-400 mt-3">
            Registrado en Seguimiento de personal (SGI) — independiente de las novedades de RH.
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4 xl:col-span-2">
          <div className="text-sm font-semibold text-gray-800 mb-3">Ausentismo por mes (días)</div>
          {absenteeismMonthly.length === 0 ? (
            <div className="text-sm text-gray-500 py-10 text-center">{loading ? 'Cargando...' : 'Sin ausentismo registrado en el periodo.'}</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={absenteeismMonthly} margin={chartMargin}>
                <CartesianGrid strokeDasharray="0" vertical={false} stroke="#e1e0d9" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<CustomTooltip suffix=" d" />} cursor={{ fill: 'rgba(227,73,73,0.06)' }} />
                <Bar dataKey="days" name="Días" fill={PALETTE.red} radius={[4, 4, 0, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {absenteeismByArea.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-4 xl:col-span-3">
            <div className="text-sm font-semibold text-gray-800 mb-3">Ausentismo por área (días)</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={absenteeismByArea} margin={chartMargin}>
                <CartesianGrid strokeDasharray="0" vertical={false} stroke="#e1e0d9" />
                <XAxis dataKey="area" tick={{ fontSize: 10, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<CustomTooltip suffix=" d" />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Bar dataKey="days" name="Días" radius={[4, 4, 0, 0]} maxBarSize={30}>
                  {absenteeismByArea.map((entry) => (
                    <Cell key={entry.area} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
